import { withTheme } from 'styled-components';
import { VictoryAxis, VictoryBar, VictoryChart, VictoryTooltip, VictoryVoronoiContainer } from 'victory';

import type { Theme } from '../../../theme/themes/ThemeTypes';
import type { PeriodBar } from '../../../utils/financesUtils';

interface FinancesBarChartProps {
  bars: PeriodBar[];
  dataKey: 'exportEarnings' | 'importCosts';
  barColor: string;
  yAxisLabel: string;
  xAxisLabel: string;
  theme: Theme;
}

const FinancesBarChart = ({ bars, dataKey, barColor, yAxisLabel, xAxisLabel, theme }: FinancesBarChartProps) => {
  const data = bars.map((bar) => ({ x: bar.label, y: bar[dataKey] / 100 }));
  const maxY = Math.max(...data.map((d) => d.y), 0);
  const resolvedMax = Math.max(maxY * 1.3, 1);
  const barWidth = Math.max(6, Math.floor(500 / Math.max(bars.length, 1)));

  return (
    <>
      <div style={{ position: 'relative', aspectRatio: '648 / 420' }}>
        <VictoryChart
          domainPadding={{ x: barWidth / 2 }}
          width={648}
          height={420}
          padding={{ top: 20, right: 40, bottom: 60, left: 70 }}
          domain={{ y: [0, resolvedMax] }}
          containerComponent={
            <VictoryVoronoiContainer
              labels={({ datum }: { datum: { y: number } }) => (datum.y > 0 ? `£${datum.y.toFixed(2)}` : '')}
              labelComponent={
                <VictoryTooltip
                  cornerRadius={4}
                  pointerLength={8}
                  style={{ fontSize: 14 }}
                  flyoutStyle={{
                    fill: theme.colors.white,
                    stroke: theme.colors.extraLight,
                  }}
                  flyoutPadding={{ top: 4, bottom: 5, left: 8, right: 8 }}
                />
              }
            />
          }
        >
          <VictoryAxis
            label={xAxisLabel}
            style={{
              axis: { stroke: theme.colors.light },
              axisLabel: { fontSize: 14, padding: 42 },
              ticks: { stroke: 'transparent' },
              tickLabels: { fontSize: 12, padding: 5 },
              grid: { stroke: 'transparent' },
            }}
          />
          <VictoryAxis
            dependentAxis
            label={`${yAxisLabel} (£)`}
            style={{
              axis: { stroke: 'transparent' },
              ticks: { stroke: 'transparent' },
              tickLabels: { fill: theme.colors.dark, fontSize: 12, padding: 5 },
              grid: {
                stroke: theme.colors.light,
                opacity: 0.4,
                strokeWidth: 0.5,
              },
              axisLabel: {
                fontSize: 14,
                padding: 50,
                textAnchor: 'middle',
                fontWeight: 'bold',
                fill: barColor,
              },
            }}
          />
          <VictoryBar data={data} barWidth={barWidth} style={{ data: { fill: barColor } }} />
        </VictoryChart>
      </div>
      {/* Spacer matching the height of Chart.jsx's LegendRow so both charts occupy identical vertical space */}
      <div style={{ height: 32 }} />
    </>
  );
};

export default withTheme(FinancesBarChart as Parameters<typeof withTheme>[0]);
